import React, { useState, useEffect } from 'react';
import { obtenerPadrePorId, actualizarUsuarioPerfil } from '../services/matriculaService';
import { Eye, EyeOff, User, Mail, Phone, Briefcase, MapPin, Building, Flag, Lock, Check } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PerfilPadre = () => {
    const [usuario, setUsuario] = useState(null);
    const [editando, setEditando] = useState(false);
    const [datosEditados, setDatosEditados] = useState({});
    const [errores, setErrores] = useState({});
    const [cambiandoPassword, setCambiandoPassword] = useState(false);
    const [nuevoPassword, setNuevoPassword] = useState('');
    const [confirmarPassword, setConfirmarPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordCriteria, setPasswordCriteria] = useState({
        length: false,
        letter: false,
        number: false,
        special: false
    });
    const [isValidPassword, setIsValidPassword] = useState(false);

    useEffect(() => {
        const allCriteriaMet = Object.values(passwordCriteria).every(criteria => criteria);
        setIsValidPassword(allCriteriaMet);
    }, [passwordCriteria]);

    useEffect(() => {
        const cargarPerfil = async () => {
            try {
                const userId = JSON.parse(localStorage.getItem('user')).id;
                const response = await obtenerPadrePorId(userId);
                setUsuario(response.padre);
                setDatosEditados(response.padre);
            } catch (error) {
                setErrores(prev => ({
                    ...prev,
                    general: 'Error al cargar el perfil'
                }));
            }
        };
        cargarPerfil();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setDatosEditados(prev => ({
            ...prev,
            [name]: value
        }));
        if (errores[name]) {
            setErrores(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const erroresValidacion = {};

        if (!datosEditados.nombre) erroresValidacion.nombre = 'El nombre es obligatorio';
        if (!datosEditados.email) erroresValidacion.email = 'El correo electrónico es obligatorio';
        if (!datosEditados.cedula) erroresValidacion.cedula = 'La cédula es obligatoria';
        if (!datosEditados.telefono) erroresValidacion.telefono = 'El teléfono es obligatorio';

        if (cambiandoPassword) {
            if (nuevoPassword !== confirmarPassword) {
                erroresValidacion.confirmarPassword = 'Las contraseñas no coinciden';
            }

            if (nuevoPassword.length <= 8) {
                erroresValidacion.nuevoPassword = 'La contraseña debe tener más de 8 caracteres';
            }

            if (passwordStrength <= 40) {
                erroresValidacion.nuevoPassword = 'La contraseña es demasiado débil';
            }
        }

        if (Object.keys(erroresValidacion).length > 0) {
            setErrores(erroresValidacion);
            return;
        }

        try {
            const datosActualizados = { ...datosEditados };
            
            if (cambiandoPassword && nuevoPassword) {
                datosActualizados.password_us = nuevoPassword;
                localStorage.setItem('passwordIsCedula', 'false');
                const event = new Event('storage');
                window.dispatchEvent(event);
            } else {
                datosActualizados.password_us = null;
            }
            
            const resultado = await actualizarUsuarioPerfil(usuario.id_usuario, datosActualizados);
            
            if (resultado.success) {
                setUsuario(resultado.padre);
                setEditando(false);
                setCambiandoPassword(false);
                setNuevoPassword('');
                setConfirmarPassword('');
                setPasswordStrength(0);
                setErrores({});
                
                // Mostrar notificación de éxito
                toast.success('Perfil actualizado correctamente', {
                    position: 'top-right',
                    autoClose: 3000,
                    style: { marginTop: '10px' }
                });
            }
        } catch (error) {
            if (error.message.includes('cédula ingresada') || error.message.includes('cédula ya está registrada')) {
                setErrores(prev => ({
                    ...prev,
                    cedula: 'La cédula ingresada ya se encuentra registrada en el sistema'
                }));
            } else if (error.message.includes('correo electrónico ingresado') || 
                      error.message.includes('correo electrónico ya está siendo utilizado') ||
                      error.code === 'EMAIL_EXISTS') {
                setErrores(prev => ({
                    ...prev,
                    email: 'El correo electrónico ingresado ya está siendo utilizado por otra cuenta'
                }));
                // Mostrar el error debajo del campo de email
                const emailInput = document.getElementById('email');
                if (emailInput) {
                    emailInput.focus();
                }
            } else {
                // Mostrar notificación de error
                toast.error(error.message || 'Error al actualizar el perfil', {
                    position: 'top-right',
                    autoClose: 3000,
                    style: { marginTop: '10px' }
                });
                
                setErrores(prev => ({
                    ...prev,
                    general: error.message || 'Error al actualizar el perfil'
                }));
            }
        }
    };

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        
        // Longitud (máximo 35 puntos)
        if (password.length >= 8) strength += 15;
        if (password.length >= 10) strength += 10;
        if (password.length >= 12) strength += 10;

        // Letras (máximo 20 puntos)
        if (password.match(/[a-z]/)) strength += 10;
        if (password.match(/[A-Z]/)) strength += 10;

        // Números (máximo 20 puntos)
        const numbers = password.match(/\d/g) || [];
        if (numbers.length >= 1) strength += 10;
        if (numbers.length >= 3) strength += 10;

        // Caracteres especiales (máximo 25 puntos)
        const specials = password.match(/[!@#$%^&*(),.?":{}|<>]/g) || [];
        if (specials.length >= 1) strength += 15;
        if (specials.length >= 2) strength += 10;

        return strength;
    };

    const getPasswordStrengthColor = (strength) => {
        if (strength < 40) return 'bg-red-500';
        if (strength < 60) return 'bg-orange-500';
        if (strength < 80) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getPasswordStrengthText = (strength) => {
        if (strength < 40) return 'Muy débil';
        if (strength < 60) return 'Débil';
        if (strength < 80) return 'Moderada';
        if (strength < 95) return 'Fuerte';
        return 'Muy fuerte';
    };

    const validatePasswordCriteria = (password) => {
        setPasswordCriteria({
            length: password.length >= 8,
            letter: /[a-zA-Z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        });
    };

    const PasswordRequirement = ({ met, text }) => (
        <div className={`flex items-center gap-2 text-xs transition-all duration-200 ${
            met ? 'hidden' : 'text-gray-400'
        }`}>
            <div className="w-3.5 h-3.5 border border-gray-300 rounded-full" />
            <span>{text}</span>
        </div>
    );

    if (!usuario) {
        return <div className="p-4 text-gray-600">Cargando perfil...</div>;
    }

    return (
        <div className="min-h-screen from-indigo-50 to-white p-8">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <div className="max-w-6xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl p-8 transform transition-all duration-300 hover:shadow-2xl">
                    <h2 className="text-3xl font-bold text-gray-800 mb-8 flex items-center gap-3">
                        <User className="w-8 h-8 text-indigo-600" />
                        Mi Perfil
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="group">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                    <User className="w-4 h-4" />
                                    Nombre Completo
                                </label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={editando ? datosEditados.nombre : usuario.nombre}
                                    onChange={handleInputChange}
                                    disabled={!editando}
                                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 
                                    ${editando ? 'bg-white border-2 border-indigo-200 text-gray-600 hover:border-indigo-400' : 'bg-gray-50 text-gray-600'} 
                                    ${errores.nombre ? 'border-red-500 ring-red-200' : ''}`}
                                />
                                {errores.nombre && (
                                    <p className="mt-1 text-xs text-red-600">{errores.nombre}</p>
                                )}
                            </div>

                            <div className="group">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                    <Mail className="w-4 h-4" />
                                    Correo Electrónico
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={editando ? datosEditados.email : usuario.email}
                                    onChange={handleInputChange}
                                    disabled={!editando}
                                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 
                                    ${editando ? 'bg-white border-2 border-indigo-200 text-gray-600 hover:border-indigo-400' : 'bg-gray-50 text-gray-600'} 
                                    ${errores.email ? 'border-red-500 ring-red-200' : ''}`}
                                />
                                {errores.email && (
                                    <p className="mt-1 text-xs text-red-600">{errores.email}</p>
                                )}
                            </div>

                            <div className="group">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                    <MapPin className="w-4 h-4" />
                                    Cédula
                                </label>
                                <input
                                    type="text"
                                    name="cedula"
                                    value={editando ? datosEditados.cedula : usuario.cedula}
                                    onChange={handleInputChange}
                                    disabled={!editando}
                                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 
                                    ${editando ? 'bg-white border-2 border-indigo-200 text-gray-600 hover:border-indigo-400' : 'bg-gray-50 text-gray-600'} 
                                    ${errores.cedula ? 'border-red-500 ring-red-200' : ''}`}
                                />
                                {errores.cedula && (
                                    <p className="mt-1 text-xs text-red-600">{errores.cedula}</p>
                                )}
                            </div>

                            <div className="group">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                    <Phone className="w-4 h-4" />
                                    Teléfono
                                </label>
                                <input
                                    type="text"
                                    name="telefono"
                                    value={editando ? datosEditados.telefono : usuario.telefono}
                                    onChange={handleInputChange}
                                    disabled={!editando}
                                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 
                                    ${editando ? 'bg-white border-2 border-indigo-200 text-gray-600 hover:border-indigo-400' : 'bg-gray-50 text-gray-600'} 
                                    ${errores.telefono ? 'border-red-500 ring-red-200' : ''}`}
                                />
                                {errores.telefono && (
                                    <p className="mt-1 text-xs text-red-600">{errores.telefono}</p>
                                )}
                            </div>

                            <div className="group">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                    <Building className="w-4 h-4" />
                                    Lugar de Trabajo
                                </label>
                                <input
                                    type="text"
                                    name="lugar_trabajo"
                                    value={editando ? datosEditados.lugar_trabajo : usuario.lugar_trabajo}
                                    onChange={handleInputChange}
                                    disabled={!editando}
                                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 
                                    ${editando ? 'bg-white border-2 border-indigo-200 text-gray-600 hover:border-indigo-400' : 'bg-gray-50 text-gray-600'} 
                                    ${errores.lugar_trabajo ? 'border-red-500 ring-red-200' : ''}`}
                                />
                            </div>

                            <div className="group">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                    <Briefcase className="w-4 h-4" />
                                    Cargo
                                </label>
                                <input
                                    type="text"
                                    name="cargo"
                                    value={editando ? datosEditados.cargo : usuario.cargo}
                                    onChange={handleInputChange}
                                    disabled={!editando}
                                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 
                                    ${editando ? 'bg-white border-2 border-indigo-200 text-gray-600 hover:border-indigo-400' : 'bg-gray-50 text-gray-600'} 
                                    ${errores.cargo ? 'border-red-500 ring-red-200' : ''}`}
                                />
                            </div>

                            <div className="group">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                    <Flag className="w-4 h-4" />
                                    Nacionalidad
                                </label>
                                <input
                                    type="text"
                                    name="nacionalidad"
                                    value={editando ? datosEditados.nacionalidad : usuario.nacionalidad}
                                    onChange={handleInputChange}
                                    disabled={!editando}
                                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 
                                    ${editando ? 'bg-white border-2 border-indigo-200 text-gray-600 hover:border-indigo-400' : 'bg-gray-50 text-gray-600'} 
                                    ${errores.nacionalidad ? 'border-red-500 ring-red-200' : ''}`}
                                />
                            </div>
                        </div>

                        {editando && (
                            <div className="mt-8 p-6 bg-gray-50 rounded-xl shadow-inner">
                                <button
                                    type="button"
                                    onClick={() => setCambiandoPassword(!cambiandoPassword)}
                                    className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors duration-200"
                                >
                                    <Lock className="w-4 h-4" />
                                    {cambiandoPassword ? 'Cancelar cambio de contraseña' : 'Cambiar contraseña'}
                                </button>
                                
                                {cambiandoPassword && (
                                    <div className="mt-4 space-y-4">
                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                                <Lock className="w-4 h-4" />
                                                Nueva Contraseña
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showNewPassword ? "text" : "password"}
                                                    value={nuevoPassword}
                                                    onChange={(e) => {
                                                        setNuevoPassword(e.target.value);
                                                        setPasswordStrength(calculatePasswordStrength(e.target.value));
                                                        validatePasswordCriteria(e.target.value);
                                                    }}
                                                    className={`w-full px-4 py-2 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 border-2 ${
                                                        nuevoPassword 
                                                            ? (isValidPassword ? 'border-green-400' : 'border-orange-300')
                                                            : 'border-indigo-200'
                                                    } hover:border-indigo-400 ${errores.nuevoPassword ? 'border-red-500 ring-red-200' : ''}`}
                                                    placeholder="••••••••"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                                >
                                                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                            {errores.nuevoPassword && (
                                                <p className="mt-1 text-xs text-red-600">{errores.nuevoPassword}</p>
                                            )}

                                            <div className="mt-2 space-y-1.5 bg-gray-50 p-3 rounded-md transition-all duration-300">
                                                <div className={`text-xs font-medium text-gray-500 mb-2 ${isValidPassword ? 'hidden' : ''}`}>
                                                    Requisitos de la contraseña:
                                                </div>
                                                <PasswordRequirement met={passwordCriteria.length} text="Mínimo 8 caracteres" />
                                                <PasswordRequirement met={passwordCriteria.letter} text="Al menos una letra" />
                                                <PasswordRequirement met={passwordCriteria.number} text="Un número" />
                                                <PasswordRequirement met={passwordCriteria.special} text="Un carácter especial" />
                                                {isValidPassword && (
                                                    <div className="flex items-center gap-2 text-xs text-green-500">
                                                        <Check size={14} />
                                                        <span>Todos los requisitos cumplidos</span>
                                                    </div>
                                                )}
                                            </div>

                                            {nuevoPassword && (
                                                <div className="mt-3">
                                                    <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-300 ${getPasswordStrengthColor(passwordStrength)}`}
                                                            style={{ width: `${passwordStrength}%` }}
                                                        />
                                                    </div>
                                                    <p className={`text-xs mt-1 text-right ${getPasswordStrengthColor(passwordStrength).replace('bg-', 'text-')}`}>
                                                        {getPasswordStrengthText(passwordStrength)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                                <Lock className="w-4 h-4" />
                                                Confirmar Nueva Contraseña
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    value={confirmarPassword}
                                                    onChange={(e) => setConfirmarPassword(e.target.value)}
                                                    className={`w-full px-4 py-2 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300 border-2 ${
                                                        confirmarPassword 
                                                            ? (confirmarPassword === nuevoPassword ? 'border-green-400' : 'border-red-300')
                                                            : 'border-indigo-200'
                                                    } hover:border-indigo-400 ${errores.confirmarPassword ? 'border-red-500 ring-red-200' : ''}`}
                                                    placeholder="••••••••"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                                >
                                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>
                                            {confirmarPassword && confirmarPassword !== nuevoPassword && (
                                                <p className="mt-1 text-xs text-red-600">Las contraseñas no coinciden</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end space-x-4 mt-8">
                            {!editando ? (
                                <button
                                    type="button"
                                    onClick={() => setEditando(true)}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
                                >
                                    Editar Perfil
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditando(false);
                                            setDatosEditados(usuario);
                                            setCambiandoPassword(false);
                                            setNuevoPassword('');
                                            setConfirmarPassword('');
                                            setPasswordStrength(0);
                                            setErrores({});
                                        }}
                                        className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
                                    >
                                        Guardar Cambios
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PerfilPadre;
